import { useState } from 'react';
import { execApi } from '../../../services/api';

// Pre-close audit: Basel runs this before approving a month. It informs, never
// blocks — catching a payroll issue before payment is worth far more than after.

interface Finding { level: 'ok' | 'warning' | 'blocker'; title: string; detail: string; who: string[] }
interface Audit {
  blockers: number; warnings: number; ready: boolean; findings: Finding[];
}

const STYLE: Record<string, { dot: string; text: string; icon: string }> = {
  ok:      { dot: 'bg-green-500',  text: 'text-green-700',  icon: '✓' },
  warning: { dot: 'bg-amber-400',  text: 'text-amber-700',  icon: '!' },
  blocker: { dot: 'bg-red-500',    text: 'text-red-700',    icon: '✕' },
};

export function AuditPanel({ month }: { month: string }) {
  const [audit, setAudit] = useState<Audit | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const run = async () => {
    setLoading(true); setOpen(true);
    try {
      const r = await execApi.getSalaryAudit(month);
      setAudit(r.data);
    } catch {
      setAudit(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={run}
        className="text-sm border border-gray-300 bg-white hover:bg-gray-50 rounded-md px-3 py-1.5"
      >
        {loading ? 'בודק…' : '✓ בדיקה לפני סגירת חודש'}
      </button>

      {open && audit && (
        <div className="mt-3 bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-100">
            <span className={`text-sm font-medium ${audit.ready ? 'text-green-700' : 'text-red-700'}`}>
              {audit.ready ? '✓ מוכן לסגירה' : '✕ יש חסמים'}
            </span>
            <span className="text-xs text-gray-500">
              {audit.blockers} חסמים · {audit.warnings} אזהרות
            </span>
          </div>
          <div className="space-y-2.5">
            {audit.findings.map((f, i) => {
              const st = STYLE[f.level];
              return (
                <div key={i} className="flex items-start gap-2.5 text-sm">
                  <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${st.dot}`} />
                  <div>
                    <span className={`font-medium ${st.text}`}>{f.title}</span>
                    {f.detail && <span className="text-gray-500"> — {f.detail}</span>}
                    {f.who.length > 0 && (
                      <div className="text-xs text-gray-500 mt-0.5">{f.who.join(' · ')}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
